import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ApiFactory } from '../../../api/agwasuite-api-factory';
import { toErrorMessage } from '../../../api/error-utils';
import { ApiUserMaintenance } from '../../../api/core/api-user-maintenance';
import { LoginGroup } from '../../../api/agwa-api-model';
import { router } from '../../../router';
import { WellKnownIds } from '../../../utility/claim-id';
import { WithUserProfileConsumer } from '../../../mixins/with-user-profile-consumer';
import styles from './user-add-styles.css?inline';
import '../../../components/agwa-page-header';
import { staffTrail } from './staff-trail';
import { terms } from '../../../language/terms';
import { normalizePhoneNumber } from '../../../utility/phone-number';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/checkbox/checkbox.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';

@customElement('user-add')
export class UserAdd extends WithUserProfileConsumer(LitElement) {
  @property({ type: String }) groupId = '';
  @property({ type: String }) returnUrl = '';

  @state() private loading = false;
  @state() private saveLoading = false;
  @state() private username = '';
  /** True once the user manually edits the username, which stops auto-sync. */
  @state() private usernameDirty = false;
  @state() private displayName = '';
  @state() private emailAddress = '';
  /** The person's phone number as typed; optional (D-151 rule 2). */
  @state() private phoneNumber = '';
  @state() private locked = false;
  @state() private saveError: string | null = null;
  @state() private imisReference = '';
  @state() private group: LoginGroup | null = null;
  @state() private groupPrefix = '';
  @state() private error: string | null = null;

  static styles = [unsafeCSS(styles)];

  connectedCallback() {
    super.connectedCallback();
    void this.fetchGroupDetails();
  }

  async fetchGroupDetails() {
    if (!this.groupId) return;

    this.loading = true;
    this.error = null;

    try {
      const api = ApiFactory.authenticated(ApiUserMaintenance);
      const resp = await api.getGroupUsers({
        loginGroupId: this.groupId,
        includeSecurityContexts: false,
      });

      if (resp.success && resp.data?.group) {
        this.group = resp.data.group;
        // Never fall back to a default prefix. If the member has none, adding users is blocked
        // until an administrator sets one - defaulting to 'agwa' is exactly the bug being fixed.
        this.groupPrefix = (this.group.userNamePrefix || '').toLowerCase();
      } else {
        this.error =
          resp.error || `Failed to load ${terms.loginGroup.lower} details`;
      }
    } catch (e: unknown) {
      this.error =
        (e as Error)?.message ||
        `Error loading ${terms.loginGroup.lower} details`;
    } finally {
      this.loading = false;
    }
  }

  private handleUsernameChange(e: CustomEvent) {
    // Keep the input to the allowed username shape as it is typed: lowercase, and only
    // letters, numbers, dot, hyphen or underscore (the server enforces the same rule).
    const raw = (e.target as HTMLInputElement).value;
    const value = raw.toLowerCase().replace(/[^a-z0-9._-]/g, '');
    this.username = value;
    // Reflect the sanitised value back into the control when it stripped something.
    (e.target as HTMLInputElement).value = value;
    // Manual entry takes over; clearing it resumes auto-generation.
    this.usernameDirty = value.trim().length > 0;
  }

  /** A username can only be added when the member has a valid prefix to derive it from. */
  private get hasPrefix(): boolean {
    return this.groupPrefix.trim().length > 0;
  }

  private handleDisplayNameChange(e: CustomEvent) {
    this.displayName = (e.target as HTMLInputElement).value;
    if (!this.usernameDirty) {
      this.username = this.deriveUsernameSuffix(this.displayName);
    }
  }

  /**
   * Derives the username suffix from a display name: lowercase, punctuation
   * dropped, whitespace-separated tokens joined with dots.
   * "John Smith Devops" -> "john.smith.devops".
   */
  private deriveUsernameSuffix(displayName: string): string {
    return displayName
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .split(/\s+/)
      .filter(Boolean)
      .join('.');
  }

  private handleEmailChange(e: CustomEvent) {
    this.emailAddress = (e.target as HTMLInputElement).value;
  }

  private handlePhoneNumberChange(e: CustomEvent) {
    this.phoneNumber = (e.target as HTMLInputElement).value;
  }

  private handleLockedChange(e: CustomEvent) {
    this.locked = (e.target as HTMLInputElement).checked;
  }

  private handleImisReferenceChange(e: CustomEvent) {
    const value = (e.target as HTMLInputElement).value;
    if (value.length <= 100) {
      this.imisReference = value;
    }
  }

  private handleBackToList() {
    this.navigateBack();
  }

  private navigateBack() {
    if (this.returnUrl) {
      void router?.navigate(this.returnUrl);
    } else if (this.isInternalGroup) {
      // AGWA staff: go to AGWA staff page
      void router?.navigate('/module/staff');
    } else {
      // Member staff: go to member workspace staff tab
      void router?.navigate(`/maintenance/companies/${this.groupId}/staff`);
    }
  }

  /** Check if this is the AGWA internal group (seeded system tenant) */
  private get isInternalGroup(): boolean {
    return this.groupId.toLowerCase() === WellKnownIds.agwaGroupId;
  }

  private async handleSave() {
    this.saveError = null;

    // The phone rule is applied before anything is sent (D-151 rule 3), in the server's own
    // words; the number goes out as it will be stored, trimmed, or not at all.
    const phone = normalizePhoneNumber(this.phoneNumber);
    if (phone.error) {
      this.saveError = phone.error;
      return;
    }

    this.saveLoading = true;

    try {
      // Compose AddGroupUserInput
      const input = {
        username: `${this.groupPrefix}.${this.username}`.toLowerCase(),
        displayName: this.displayName,
        emailAddress: this.emailAddress,
        phoneNumber: phone.value ?? undefined,
        claims: {},
        loginGroupId: this.groupId,
        locked: this.locked,
        imisDetails: {
          reference: this.imisReference,
          properties: {}, // always empty for now
        },
      };

      const api = ApiFactory.authenticated(ApiUserMaintenance);
      const result = await api.addGroupUser(input);

      if (!result.success) {
        // The API client resolves the server's term tags for every failed response, so this
        // reads as a sentence without the screen having to remember to resolve it.
        this.saveError = toErrorMessage(result);
        return;
      }

      // Navigate back to the staff list after successful save
      this.navigateBack();
    } catch (err: unknown) {
      this.saveError =
        (err as Error)?.message || 'An unexpected error occurred.';
    } finally {
      this.saveLoading = false;
    }
  }

  render() {
    if (this.loading) {
      return html`<div class="loading">
        Loading ${terms.loginGroup.lower} details...
      </div>`;
    }

    if (this.error) {
      return html`
        <div class="error">
          <p>${this.error}</p>
          <sl-button @click=${this.handleBackToList}
            >Back to ${terms.loginUser.value} List</sl-button
          >
        </div>
      `;
    }

    if (!this.group) {
      return html`
        <div class="not-found">
          <p>${terms.loginGroup.value} not found or data not available</p>
          <sl-button @click=${this.handleBackToList}
            >Back to ${terms.loginUser.value} List</sl-button
          >
        </div>
      `;
    }

    return html`
      <div class="user-add">
        <agwa-page-header .crumbs=${staffTrail(this.group)}>
          New ${terms.loginUser.value}
        </agwa-page-header>

        <div class="add-user-content">
          ${
            this.saveError
              ? html`<sl-alert variant="danger" open
                  >${this.saveError}</sl-alert
                >`
              : null
          }
          ${
            !this.hasPrefix
              ? html`<sl-alert variant="warning" open>
                  This ${terms.loginGroup.lower} has no ${terms.loginUser.lower}
                  prefix set, so ${terms.loginUser.lowerPlural} cannot be added
                  yet. An administrator must set the
                  ${terms.loginGroup.lowerPossessive} ${terms.loginUser.lower}
                  prefix before adding ${terms.loginUser.lowerPlural}.
                </sl-alert>`
              : null
          }

          <!-- User Details Section -->
          <section>
            <h3>${terms.loginUser.value} Details</h3>
            <sl-input
              label="Display Name"
              value=${this.displayName}
              @sl-input=${this.handleDisplayNameChange}
            ></sl-input>
            <sl-input
              label="Username"
              value=${this.username}
              @sl-input=${this.handleUsernameChange}
              required
              ?disabled=${!this.hasPrefix}
              help-text="Generated from the display name (lowercase). Edit to override."
              placeholder="firstname.surname"
            >
              <span
                slot="prefix"
                class="group-prefix-label"
                aria-label="${terms.loginGroup.value} Prefix"
                >${this.groupPrefix}.</span
              >
            </sl-input>
            <div class="rules-legend">
              <strong>Username &amp; prefix rules</strong>
              <ul>
                <li>
                  Every username starts with this
                  ${terms.loginGroup.lowerPossessive} prefix:
                  <code>${this.groupPrefix || '(not set)'}.</code>
                </li>
                <li>
                  Lowercase only - letters, numbers, dot (.), hyphen (-) or
                  underscore (_); no spaces.
                </li>
                <li>
                  The prefix itself uses letters, numbers, hyphen or underscore
                  (no dots or spaces) and cannot be changed once
                  ${terms.loginUser.lowerPlural} exist.
                </li>
              </ul>
            </div>
            <sl-input
              label="Email"
              value=${this.emailAddress}
              @sl-input=${this.handleEmailChange}
            ></sl-input>
            <!-- Under Email, optional (D-151 rule 2). The rule's refusal shows as it is typed. -->
            <sl-input
              label="Phone"
              type="tel"
              value=${this.phoneNumber}
              help-text=${normalizePhoneNumber(this.phoneNumber).error ?? ''}
              @sl-input=${this.handlePhoneNumberChange}
            ></sl-input>
            <sl-checkbox
              ?checked=${this.locked}
              @sl-change=${this.handleLockedChange}
              class="locked-checkbox"
            >
              Blocked
            </sl-checkbox>
          </section>

          <!-- iMIS Details Section -->
          <section>
            <h3>${terms.imis.value} Details</h3>
            <sl-input
              label="${terms.imis.value} Reference"
              value=${this.imisReference}
              maxlength="100"
              @sl-input=${this.handleImisReferenceChange}
              placeholder="Enter ${terms.imis.value} reference"
            ></sl-input>
          </section>

          <section>
            <h3>Access & Features</h3>
            <sl-alert variant="primary" open>
              Role and feature assignments are now derived from the security
              catalog. Create the ${terms.loginUser.lower} first, then apply
              role sets from the ${terms.userGroupDomain.lower} tools.
            </sl-alert>
          </section>

          <div class="add-user-footer">
            <sl-button
              variant="neutral"
              @click=${this.handleBackToList}
              ?disabled=${this.saveLoading}
            >
              Cancel
            </sl-button>
            <sl-button
              variant="primary"
              @click=${this.handleSave}
              ?loading=${this.saveLoading}
              ?disabled=${!this.username || !this.hasPrefix}
            >
              Add ${terms.loginUser.value}
            </sl-button>
          </div>
        </div>
      </div>
    `;
  }
}
